import React from "react";
import { IndianRupee, Package, Edit2, Trash2 } from "lucide-react";
import { ProductWithStock, getStockStatusColor } from "@/types/inventory";

type ProductCardProps = {
  product: ProductWithStock;
  onEdit?: (product: ProductWithStock) => void;
  onDelete?: (product: ProductWithStock) => void;
};

export function ProductCard({ product, onEdit, onDelete }: ProductCardProps) {
  const productDisplay = [product.brand, product.dosage, product.form]
    .filter(Boolean)
    .join(" • ");

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">{product.name}</h3>
          {productDisplay && (
            <p className="text-sm text-gray-500">{productDisplay}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${getStockStatusColor(
              product.stock_status
            )}`}
          >
            {product.current_stock} {product.unit_of_measure || "units"}
          </span>
          {(onEdit || onDelete) && (
            <div className="flex gap-1">
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(product);
                  }}
                  className="p-1.5 text-gray-600 hover:text-[#5B8E87] hover:bg-gray-50 rounded transition-colors"
                  title="Edit product"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(product);
                  }}
                  className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Delete product"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        {product.default_selling_price && (
          <div className="flex items-center gap-1 text-gray-700">
            <IndianRupee className="w-4 h-4" />
            <span className="font-medium">
              {Number(product.default_selling_price).toFixed(2)}
            </span>
            <span className="text-xs text-gray-500">per unit</span>
          </div>
        )}

        {product.stock_status === "low" && (
          <span className="text-xs text-orange-600 font-medium">Low stock</span>
        )}
        {product.stock_status === "out" && (
          <span className="text-xs text-red-600 font-medium">Out of stock</span>
        )}
      </div>
    </div>
  );
}
